import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpTrayIcon, PaperAirplaneIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { hasAnyRole } from "../../utils/auth";
import { ROLES } from "../../auth/roles";
import { Button, EmptyState, Loading, RowAction, Tag, Textarea } from "../ui";
import { errorMessageOf, scholarshipsApi } from "../../api/scholarships";
import { SCHOLARSHIPS_KEY } from "./model";
import { fileSummary, formatDateTime, openDocument } from "./review/model";
import { DocumentRow } from "./review/ApplicationContents";

// Requests for more once the application has left HEC: the sponsor asks, the student answers in the portal
export const NOMINEE_STATES = ["WITH_SPONSOR", "GRANTED", "REJECTED"];
const NOTE_MAX = 2000;
const ACCEPT = "application/pdf,image/jpeg,image/png";

const requestsKey = (side, applicationId) => [...SCHOLARSHIPS_KEY, "document-requests", side, applicationId];
const STATES = { OPEN: { label: "Waiting for the student", color: "yellow" }, ANSWERED: { label: "Answered", color: "green" } };

const useRequests = (side, applicationId) =>
  useQuery({
    queryKey: requestsKey(side, applicationId),
    queryFn: async () => (await scholarshipsApi.get(`${side}/applications/${applicationId}/document-requests`)).data,
  });

// The files sent for one request, previewed like an application's documents through a short-lived link
function Files({ request, side, applicationId, onRemove, busy }) {
  if (!request.files.length) return null;
  return (
    <ul className="flex flex-col divide-y divide-slate-100">
      {request.files.map((f) => (
        <DocumentRow
          key={f.id}
          title={f.fileName}
          detail={`${fileSummary(f)} · ${formatDateTime(f.uploadedAt)}`}
          onOpen={() => openDocument(`${side}/applications/${applicationId}/document-requests/${request.id}/files/${f.id}/download-url`, f.fileName)}
          actions={onRemove && <RowAction Icon={TrashIcon} label={`Remove ${f.fileName}`} variant="ghost" onClick={() => onRemove(f)} disabled={busy} />}
        />
      ))}
    </ul>
  );
}

function RequestHeader({ request }) {
  const state = STATES[request.state];
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs text-slate-500">Asked {formatDateTime(request.requestedAt)}</span>
      <Tag color={state.color}>{state.label}</Tag>
    </div>
  );
}

/**
 * Staff side, in the nominee's application dialog: the scholarship owner asks the student for
 * documents or information; the HEC Scholarship Admin only reads the requests and the answers.
 */
export function StaffDocumentRequests({ applicationId }) {
  const queryClient = useQueryClient();
  const { data: requests = [], isPending, isError, refetch } = useRequests("staff", applicationId);
  const [note, setNote] = useState("");
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const asks = hasAnyRole(ROLES.SCHOLARSHIP_OWNER);

  const ask = async () => {
    if (!note.trim()) return setError("Say what the student must send.");
    setSending(true);
    try {
      await scholarshipsApi.post(`staff/applications/${applicationId}/document-requests`, { note: note.trim() });
      toast.success("Sent to the student");
      setNote("");
      await queryClient.invalidateQueries({ queryKey: requestsKey("staff", applicationId) });
    } catch (err) {
      console.error(`Failed to ask for documents on application ${applicationId}:`, err);
      toast.error(errorMessageOf(err, "Failed to send the request. Please try again."));
    } finally {
      setSending(false);
    }
  };

  if (isPending) return <Loading label="Loading the requests…" />;
  if (isError) return <EmptyState title="Could not load the requests" onRefresh={refetch} />;

  return (
    <div className="flex flex-col gap-4">
      {asks && (
        <div className="flex flex-col gap-3 rounded-lg border border-slate-100 p-4">
          <Textarea
            label="Ask the student for more"
            rows={3}
            maxLength={NOTE_MAX}
            placeholder="e.g. A certified copy of your passport and a bank statement"
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setError(null);
            }}
            error={error}
            disabled={sending}
          />
          <Button className="self-end" onClick={ask} loading={sending} icon={<PaperAirplaneIcon className="h-4 w-4" />}>
            Send to the student
          </Button>
        </div>
      )}
      {requests.length === 0 ? (
        <p className="text-sm text-slate-500">Nothing has been asked yet.</p>
      ) : (
        <ol className="flex flex-col gap-3">
          {[...requests].reverse().map((r) => (
            <li key={r.id} className="flex flex-col gap-2 rounded-lg border border-slate-100 p-4">
              <RequestHeader request={r} />
              <p className="whitespace-pre-line text-sm text-slate-800">{r.note}</p>
              {r.state === "ANSWERED" && (
                <div className="flex flex-col gap-2 border-t border-slate-100 pt-2">
                  <span className="text-xs text-slate-500">Answered {formatDateTime(r.answeredAt)}</span>
                  {r.answerNote && <p className="whitespace-pre-line text-sm italic text-slate-700">“{r.answerNote}”</p>}
                  <Files request={r} side="staff" applicationId={applicationId} />
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

// One open request on the student's side: files go up straight away, the answer is sent once
function OpenRequest({ request, applicationId, onChanged }) {
  const input = useRef(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const path = `student/applications/${applicationId}/document-requests/${request.id}`;

  const run = async (action, success, failure) => {
    setBusy(true);
    try {
      await action();
      if (success) toast.success(success);
      await onChanged();
    } catch (err) {
      console.error(`Document request ${request.id}: ${failure}`, err);
      toast.error(errorMessageOf(err, `${failure} Please try again.`));
    } finally {
      setBusy(false);
    }
  };

  const upload = (file) => {
    const body = new FormData();
    body.append("file", file);
    return run(() => scholarshipsApi.post(`${path}/files`, body), `${file.name} added`, "Failed to add the file.");
  };
  const remove = (f) => run(() => scholarshipsApi.delete(`${path}/files/${f.id}`), "File removed", "Failed to remove the file.");
  const send = () =>
    run(() => scholarshipsApi.post(`${path}/answer`, { note: note.trim() || null }, { ifMatch: `"${request.version}"` }), "Sent to the partner", "Failed to send.");

  return (
    <div className="flex flex-col gap-3 border-t border-slate-100 pt-3">
      <Files request={request} side="student" applicationId={applicationId} onRemove={remove} busy={busy} />
      <input
        ref={input}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = "";
          if (file) upload(file);
        }}
      />
      <Button variant="outline" size="sm" className="self-start" onClick={() => input.current?.click()} disabled={busy || request.files.length >= 10} icon={<ArrowUpTrayIcon className="h-4 w-4" />}>
        Add a file (PDF, JPEG or PNG, up to 10 MB)
      </Button>
      <Textarea label="Note to the partner (optional)" rows={2} maxLength={NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} />
      <Button className="self-end" onClick={send} loading={busy} disabled={!request.files.length && !note.trim()} icon={<PaperAirplaneIcon className="h-4 w-4" />}>
        Send to the partner
      </Button>
    </div>
  );
}

// Student side, on the application page: what the sponsor asked for, answered here; nothing shows until they ask
export function StudentDocumentRequests({ applicationId }) {
  const queryClient = useQueryClient();
  const { data: requests = [] } = useRequests("student", applicationId);
  if (!requests.length) return null;
  const refresh = () => queryClient.invalidateQueries({ queryKey: requestsKey("student", applicationId) });
  const open = requests.filter((r) => r.state === "OPEN").length;

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-amber-200 bg-white p-5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-semibold text-slate-900">Requests from the partner</h3>
        {open > 0 && <Tag color="yellow">{open} waiting for you</Tag>}
      </div>
      <ol className="flex flex-col gap-4">
        {[...requests].reverse().map((r) => (
          <li key={r.id} className="flex flex-col gap-2 rounded-lg border border-slate-100 p-4">
            <RequestHeader request={r} />
            <p className="whitespace-pre-line text-sm text-slate-800">{r.note}</p>
            {r.state === "OPEN" ? (
              <OpenRequest request={r} applicationId={applicationId} onChanged={refresh} />
            ) : (
              <div className="flex flex-col gap-2 border-t border-slate-100 pt-2">
                <span className="text-xs text-slate-500">You answered {formatDateTime(r.answeredAt)}</span>
                {r.answerNote && <p className="whitespace-pre-line text-sm italic text-slate-700">“{r.answerNote}”</p>}
                <Files request={r} side="student" applicationId={applicationId} />
              </div>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
